import React, { useEffect, useState } from 'react';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { TicketInterface } from '../../../../models/TicketModule/Ticket';
import { Table, TableHead, TableRow, TableCell, TableBody, TextField, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import StatusBadge from '../../../../common/StatusBadge';
import ToastMessage from '../../../../common/ToastMessage';
import useUsers from '../../../../store/ReactQueries/users/useUser';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import useStatuses from '../../../../store/ReactQueries/statuses/useStatus';
import useTicketById from '../../../../store/ReactQueries/tickets/useTicketById';
import usePriorities from '../../../../store/ReactQueries/priorities/usePriority';
import useDeleteTicket from '../../../../store/ReactQueries/tickets/useDeleteTicket';

const paddingInput = '8px 14px';
const tableCellStyle = { paddingTop: '6px', paddingBottom: '6px', fontWeight: 'bold', fontSize: 16 };
const paddingHorizontal = { paddingLeft: '5px', paddingRight: '5px', paddingTop: '8px', paddingBottom: '8px' };

interface SubTicketTableProps {
    view: boolean;
    subTickets: TicketInterface[];
    masterTicketId: string | undefined;
    setHasSubTicketsError: React.Dispatch<React.SetStateAction<boolean>>;
    setSubTickets: React.Dispatch<React.SetStateAction<TicketInterface[]>>;
}

const SubTicketTable = ({ subTickets, view, masterTicketId, setHasSubTicketsError, setSubTickets }: SubTicketTableProps) => {
    const { language } = useLanguageStore();
    const { data: users } = useUsers(0, 0);
    const { data: statuses } = useStatuses(0, 0);
    const { data: priorities } = usePriorities(0, 0);
    const { data: masterTicket } = useTicketById(masterTicketId);

    const deleteTicket = useDeleteTicket(0, 0);

    const [errors, setErrors] = useState<Record<number, Partial<Record<keyof TicketInterface, string>>>>({});

    const hasActiveErrors = (errors: Record<number, Partial<Record<keyof TicketInterface, string>>>): boolean => {
        for (const key in errors) {
            if (Object.keys(errors[key]).length > 0) {
                return true;
            }
        }
        return false;
    };

    const getValidationErrors = (ticket: TicketInterface): Partial<Record<keyof TicketInterface, string>> => {
        const newErrors: Partial<Record<keyof TicketInterface, string>> = {};
        if (!ticket.title || ticket.title.trim().length < 2) {
            newErrors.title = 'Title must be at least 2 characters';
        }
        if (!ticket.description || ticket.description.trim().length < 2) {
            newErrors.description = 'Description must be at least 2 characters';
        }
        if (!ticket.dueDate) {
            newErrors.dueDate = 'Due date is required';
        }
        if (!ticket.priorityId) {
            newErrors.priorityId = 'Priority is required';
        }
        if (!ticket.statusId) {
            newErrors.statusId = 'Status is required';
        }
        if (!ticket.assignedToId) {
            newErrors.assignedToId = 'Assigned user is required';
        }
        return newErrors;
    };

    const handleAddTicket = () => {
        const newTicket: TicketInterface = {
            id: '',
            title: '',
            description: '',
            customerId: masterTicket?.customerId ?? '',
            interventionTypeId: masterTicket?.interventionTypeId ?? '',
            locationId: masterTicket?.locationId ?? '',
            categoryId: masterTicket?.categoryId ?? '',
            contractorId: masterTicket?.contractorId ?? '',
            priorityId: '',
            statusId: '',
            assignedToId: '',
            assetId: masterTicket?.assetId ?? '',
            dueDate: undefined,
            tenantId: masterTicket?.tenantId ?? '',
            authorId: masterTicket?.authorId ?? '',
            departmentId: masterTicket?.departmentId ?? '',
            formImages: masterTicket?.formImages ?? [],
            ticketTimelines: masterTicket?.ticketTimelines ?? [],
            images: masterTicket?.images ?? [],
            ticketFinancialType: masterTicket?.ticketFinancialType ?? 0,
            financialAssignedToId: masterTicket?.financialAssignedToId ?? '',
            ticketNo: masterTicket?.ticketNo ?? 0,
            companyId: masterTicket?.companyId ?? '',
            isRecurring: false,
            isMasterTicket: false,
            masterTicketId: masterTicketId,
        };

        setSubTickets((prev) => [...prev, newTicket]);

        const newError = getValidationErrors(newTicket);
        setErrors(prev => ({
            ...prev,
            [subTickets.length]: newError
        }));
    };

    const handleChange = (
        index: number,
        field: keyof TicketInterface,
        value: any,
    ) => {
        const updated = [...subTickets];
        updated[index] = { ...updated[index], [field]: value };
        setSubTickets(updated);

        const updatedErrors = getValidationErrors(updated[index]);
        setErrors(prev => ({ ...prev, [index]: updatedErrors }));
    };

    const handleRemoveTicket = async (index: number, id: string | undefined) => {
        const updated = [...subTickets];

        if (id && id !== '') {
            const confirmed = await ToastMessage.Confirm(language["ToastDeleteItem"]);
            if (!confirmed) return;

            deleteTicket.mutate(id, {
                onSuccess: () => {
                    updated.splice(index, 1);
                    setSubTickets(updated);
                    setErrors(prevErrors => {
                        const newErrors: typeof prevErrors = {};

                        Object.keys(prevErrors).forEach(key => {
                            const keyNum = Number(key);
                            if (keyNum < index) {
                                newErrors[keyNum] = prevErrors[keyNum];
                            } else if (keyNum > index) {
                                newErrors[keyNum - 1] = prevErrors[keyNum];
                            }
                        });

                        return newErrors;
                    });
                    ToastMessage.Success('SubTicket deleted successfully');
                },
                onError: () => ToastMessage.Failed(),
            });
        } else {
            updated.splice(index, 1);
            setSubTickets(updated);
            setErrors(prevErrors => {
                const newErrors: typeof prevErrors = {};

                Object.keys(prevErrors).forEach(key => {
                    const keyNum = Number(key);
                    if (keyNum < index) {
                        newErrors[keyNum] = prevErrors[keyNum];
                    } else if (keyNum > index) {
                        newErrors[keyNum - 1] = prevErrors[keyNum];
                    }
                });

                return newErrors;
            });
        }
    };

    const tableCellFields: string[] = ['Title', 'Description', 'DueDate', 'Priority', 'AssignedTo', 'Status'];

    useEffect(() => {
        if (masterTicket?.subTickets) {
            setSubTickets(masterTicket.subTickets);
        }
    }, [masterTicket?.subTickets]);

    useEffect(() => {
        if (setHasSubTicketsError) {
            setHasSubTicketsError(hasActiveErrors(errors));
        }
    }, [errors, setHasSubTicketsError]);

    return (
        <div className='d-flex flex-column gap-1 mt-2 mb-4'>
            <div className='d-flex flex-row align-items-center'>
                <h5 className='mb-0'>Sub Tickets</h5>
                {
                    !view
                    && <IconButton
                        color='primary'
                        title='Add Sub Ticket'
                        aria-label="addsubticket"
                        sx={{ marginLeft: 'auto', marginBottom: 0.5 }}
                        onClick={handleAddTicket}
                    >
                        <AddIcon />
                    </IconButton>
                }
            </div>
            <div style={{ overflowX: 'auto' }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            {
                                tableCellFields.map(field =>
                                    <TableCell key={field} sx={tableCellStyle}>{language[field] ?? field}</TableCell>)
                            }
                            {!view && <TableCell sx={tableCellStyle} />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            subTickets?.length === 0
                                ? <TableRow>
                                    <TableCell colSpan={tableCellFields.length + 1} sx={{ textAlign: 'center' }}>
                                        {language["NoDataAvailable"]}
                                    </TableCell>
                                </TableRow>
                                : subTickets.map((ticket, index) => {
                                    let existingTicket = ticket.id !== '' && ticket.id !== undefined;
                                    return <TableRow key={index}>
                                        <TableCell width={'15%'} sx={paddingHorizontal}>
                                            <TextField
                                                error={!!errors[index]?.title}
                                                // helperText={errors[index]?.title ? 'Title is required' : ''}
                                                disabled={view || existingTicket}
                                                title={ticket.title ?? ''}
                                                variant="outlined"
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-input': {
                                                        padding: paddingInput,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: existingTicket ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                                    },
                                                    '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                                                        WebkitTextFillColor: 'black !important',
                                                    },
                                                }}
                                                value={ticket.title ?? ''}
                                                onChange={(e) => handleChange(index, 'title', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell width={'22%'} sx={paddingHorizontal}>
                                            <TextField
                                                variant="outlined"
                                                error={!!errors[index]?.description}
                                                value={ticket.description ?? ''}
                                                title={ticket.description ?? ''}
                                                disabled={view || existingTicket}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-input': {
                                                        padding: paddingInput,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: existingTicket ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                                    },
                                                    '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                                                        WebkitTextFillColor: 'black !important',
                                                    },
                                                }}
                                                onChange={(e) => handleChange(index, 'description', e.target.value)}
                                            />
                                        </TableCell>
                                        <TableCell width={'2%'} sx={paddingHorizontal}>
                                            <TextField
                                                type="date"
                                                error={!!errors[index]?.dueDate}
                                                disabled={view || existingTicket}
                                                variant="outlined"
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-input': {
                                                        padding: paddingInput,
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: existingTicket ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                                    },
                                                    '& .css-1t8l2tu-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                                                        WebkitTextFillColor: 'black !important',
                                                    },
                                                }}
                                                value={
                                                    ticket.dueDate && !isNaN(new Date(ticket.dueDate).getTime())
                                                        ? new Date(ticket.dueDate).toISOString().split('T')[0]
                                                        : ''
                                                }
                                                onChange={(e) => handleChange(index, 'dueDate', e.target.value ? new Date(e.target.value) : undefined)}
                                            />
                                        </TableCell>
                                        <TableCell width={'10%'} sx={paddingHorizontal}>
                                            <Select
                                                variant="outlined"
                                                disabled={view || existingTicket}
                                                error={!!errors[index]?.priorityId}
                                                IconComponent={view || existingTicket ? () => null : undefined}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-input': {
                                                        padding: paddingInput,
                                                    },
                                                    '& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                                                        WebkitTextFillColor: 'black !important',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: existingTicket ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                                    },
                                                }}
                                                value={ticket.priorityId}
                                                onChange={(e) => handleChange(index, 'priorityId', e.target.value)}
                                            >
                                                {
                                                    priorities?.map((priority) => (
                                                        <MenuItem key={priority.id} value={priority.id}>
                                                            {priority.title}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>

                                        </TableCell>
                                        <TableCell width={'10%'} sx={paddingHorizontal}>
                                            <Select
                                                variant="outlined"
                                                disabled={view || existingTicket}
                                                value={ticket.assignedToId || ''}
                                                error={!!errors[index]?.assignedToId}
                                                IconComponent={view || existingTicket ? () => null : undefined}
                                                onChange={(e) => handleChange(index, 'assignedToId', e.target.value)}
                                                sx={{
                                                    width: '100%',
                                                    '& .MuiInputBase-input': {
                                                        padding: paddingInput,
                                                    },
                                                    '& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                                                        WebkitTextFillColor: 'black !important',
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: existingTicket ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                                    },
                                                }}
                                            >
                                                {
                                                    users?.map((user) => (
                                                        <MenuItem key={user.id} value={user.id}>
                                                            {`${user.firstname} ${user.lastname}`}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </Select>
                                        </TableCell>
                                        <TableCell width={'10%'} sx={paddingHorizontal}>
                                            {
                                                view || existingTicket
                                                    ? <StatusBadge
                                                        fontSize={12}
                                                        width={"fit-content"}
                                                        color={ticket.status?.badgeColor || '#8D97A0'}
                                                        name={ticket.status && ticket.status.title ? ticket.status.title : "No Status"}
                                                    />
                                                    : <Select
                                                        variant="outlined"
                                                        value={ticket.statusId}
                                                        error={!!errors[index]?.statusId}
                                                        IconComponent={view || existingTicket ? () => null : undefined}
                                                        onChange={(e) => handleChange(index, 'statusId', e.target.value)}
                                                        sx={{
                                                            width: '100%',
                                                            '& .MuiInputBase-input': {
                                                                padding: paddingInput,
                                                            },
                                                            '& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                                                                WebkitTextFillColor: 'black !important',
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                border: existingTicket ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                                                            },
                                                        }}
                                                    >
                                                        {
                                                            statuses?.map((status) => (
                                                                <MenuItem key={status.id} value={status.id}>
                                                                    {status.title}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </Select>
                                            }
                                        </TableCell>
                                        <TableCell width={'2%'} sx={paddingHorizontal}>
                                            {
                                                !view
                                                && <IconButton
                                                    color='error'
                                                    aria-label="edit"
                                                    title='Delete Ticket'
                                                    disabled={ticket.status?.isCompleted}
                                                    sx={{ marginLeft: 'auto', marginBottom: 0.5 }}
                                                    onClick={() => handleRemoveTicket(index, ticket.id)}
                                                >
                                                    <DeleteOutlinedIcon />
                                                </IconButton>
                                            }
                                        </TableCell>
                                    </TableRow>
                                })
                        }
                    </TableBody>
                </Table>

            </div>
        </div>
    );
};

export default SubTicketTable;